/**
 * Field-level diff for a single string between two CONSECUTIVE versions of its
 * group — a version and the one immediately before it in the version list.
 *
 * `computeVersionDiff` in the string-group-detail feature answers a different
 * question — which strings in a group were added/edited/removed — and reports a
 * whole string as `changed` without saying which field moved. The string detail
 * page needs the per-field answer for exactly two fields, so it compares them
 * directly instead.
 */
export interface FieldDiff {
  /** True when the two versions hold different text. */
  changed: boolean;
  /** The PREDECESSOR version's value, normalized to '' when absent. */
  previous: string;
  /** THIS version's value, normalized to '' when absent. */
  value: string;
}

/**
 * Compares one text field between a version and its predecessor, so `changed`
 * means "this version changed the field", not "it differs from today".
 *
 * Null and undefined are normalized to '' so "field absent" and "field empty"
 * do not read as a change.
 */
export function diffTextField(
  previous: string | null | undefined,
  value: string | null | undefined,
): FieldDiff {
  const previousValue = previous ?? '';
  const thisValue = value ?? '';
  return {
    changed: previousValue !== thisValue,
    previous: previousValue,
    value: thisValue,
  };
}

/**
 * Picks which of the group's ACTUAL versions to show, newest first and capped at
 * `limit`. Only versions earlier than `currentVersion` are listed; each is then
 * compared with the next one down in this same list.
 *
 * The versions are supplied by `listStringGroupVersions` rather than derived as
 * N-1 … 1: group versions are not guaranteed to be a contiguous range, so
 * synthesizing them both requests versions that may not exist and scales the
 * request count with the version number.
 *
 * The API returns the current version as a string; a value that is not a
 * positive integer yields an empty list rather than a bad query, so the panel
 * degrades to its empty state instead of firing nonsense requests.
 */
export function earlierVersionsToCompare(
  versions: readonly number[],
  currentVersion: string,
  limit: number,
): number[] {
  const parsed = parseInt(currentVersion, 10);
  if (Number.isNaN(parsed)) {
    return [];
  }
  return versions
    .filter((version) => version < parsed)
    .sort((a, b) => b - a)
    .slice(0, limit);
}
